import * as React from 'react'
import moment from 'moment'
import { orderBy } from 'lodash'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { API, IInventoryRestockReport } from '@getgreenline/homi-shared'
import { Skeleton } from 'antd'
import { Currency } from '../../../../../../components/Currency'
import { parseAndDownloadCSV } from '../../../../../../utilities/helpers'
import { ReportFilter } from '../../ReportFilter'
import { ReportFilterStore } from '../../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../../Track'
import { Alert } from 'antd-v4'
import { ArrowRightOutlined } from '@ant-design/icons'
import { browserHistory } from 'react-router'
import { AdvancedReportRoutes, InsightsRoutes, Routes } from '../../../../../../constants/Routes'
import { ColumnHeaders } from './types'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  reportFilterStore?: ReportFilterStore
}

type ProductSortFields =
  | 'productName'
  | 'categoryName'
  | 'supplierName'
  | 'locationName'
  | 'currentQuantity'
  | 'restockQuantity'
  | 'lowStockQuantity'

interface State {
  restockReport?: IInventoryRestockReport
  productSort: { field: ProductSortFields; direction: 'asc' | 'desc' }
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class RestockReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      productSort: {
        field: 'productName',
        direction: 'asc',
      },
    }
  }

  async componentDidMount() {
    this.loadReport()
  }

  loadReport() {
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!
    this.setState({ restockReport: undefined })
    const filter = {
      locationIds: selectedLocationIds,
      startDate: moment(startDate).startOf('day').toDate(),
      endDate: moment(endDate).endOf('day').toDate(),
    }
    const company = this.props.currentCompanyStore!.company!
    Track.startTimer('dashboard:reports_restock')
    API.getInventoryRestockReport(company.id, filter).then((report) => {
      Track.track('dashboard:reports_restock', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ restockReport: report })
    })
  }

  toggleProductSort(field: ProductSortFields) {
    if (this.state.productSort.field === field) {
      if (this.state.productSort.direction === 'asc') {
        this.setState({
          productSort: {
            field: field,
            direction: 'desc',
          },
        })
      } else {
        this.setState({
          productSort: {
            field: field,
            direction: 'asc',
          },
        })
      }
    } else {
      this.setState({
        productSort: {
          field: field,
          direction: 'desc',
        },
      })
    }
  }

  getSortedReport() {
    if (this.state.restockReport) {
      return orderBy(
        this.state.restockReport.data.products,
        [this.state.productSort.field],
        [this.state.productSort.direction],
      )
    }
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const restockReport = this.getSortedReport()
    const filename = `restock-report-${startDate}-to-${endDate}`

    if (restockReport) {
      const reportJSON: any[] = []

      restockReport.forEach((product) => {
        reportJSON.push({
          product: product.productName,
          sku: product.sku,
          category: product.categoryName,
          brand: product.supplierName,
          current: product.currentQuantity,
          lowStock: product.lowStockQuantity,
          restock: product.restockQuantity,
          quantityToOrder: product.restockQuantity - product.currentQuantity,
          cost: product.purchaseTotal,
        })
      })

      parseAndDownloadCSV(filename, reportJSON)
    }
  }

  toReports = () => {
    const { reportFilterStore, currentCompanyStore } = this.props
    if (!reportFilterStore || !currentCompanyStore || !currentCompanyStore.company) {
      return
    }

    reportFilterStore.setIsFirstLoad(true)
    browserHistory.push({
      pathname: Routes.INSIGHTS_REPORTS(
        currentCompanyStore.company.id,
        InsightsRoutes.INVENTORY_REORDER,
      ),
      query: {
        locationIds: reportFilterStore.selectedLocationIds,
      },
    })
  }

  render() {
    const restockReport = this.state.restockReport

    if (!restockReport) {
      return <Skeleton active />
    }

    const orderedRestockReport = this.getSortedReport() || []
    return (
      <>
        <Helmet>
          <title>Reports | Inventory Restock</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter maxRangeMonths={12} hideDates />

        <Alert
          className='mb-3'
          message={
            <a style={{ color: 'black', textDecorationLine: 'underline' }} onClick={this.toReports}>
              For restock recommendations based on sales <strong>go here </strong>
              <ArrowRightOutlined />
            </a>
          }
          type='success'
        />

        <div className='rounded background-white p-3'>
          <div className='d-flex flex-row justify-content-between'>
            <div className='text-muted'>As of {moment().format('LLL')}</div>
            <button className='btn btn-sm btn-light pull-right mb-3' onClick={() => this.export()}>
              Export as csv
            </button>
          </div>

          <div className='table-responsive'>
            <div>
              <b>Inventory restock</b>
            </div>
            <div className='text-muted'>Based on low-stock and restock levels per location</div>

            <table className='table table-sm'>
              <thead>
                <tr>
                  <th>
                    <a onClick={() => this.toggleProductSort('productName')}>Product</a>
                  </th>
                  <th>
                    <a onClick={() => this.toggleProductSort('supplierName')}>
                      {ColumnHeaders.BRAND}
                    </a>
                  </th>
                  <th>
                    <a onClick={() => this.toggleProductSort('categoryName')}>
                      {ColumnHeaders.CATEGORY}
                    </a>
                  </th>
                  <th>
                    <a onClick={() => this.toggleProductSort('locationName')}>
                      {ColumnHeaders.LOCATION}
                    </a>
                  </th>
                  <th className='text-right'>
                    <a onClick={() => this.toggleProductSort('currentQuantity')}>
                      {ColumnHeaders.CURRENT_QTY}
                    </a>
                  </th>
                  <th className='text-right' style={{ whiteSpace: 'nowrap' }}>
                    <a onClick={() => this.toggleProductSort('lowStockQuantity')}>
                      {ColumnHeaders.LOW_STOCK_QTY}
                    </a>
                  </th>
                  <th className='text-right'>
                    <a onClick={() => this.toggleProductSort('restockQuantity')}>
                      {ColumnHeaders.RESTOCK_QTY}
                    </a>
                  </th>
                  <th className='text-right'>{ColumnHeaders.QTY_TO_ORDER}</th>
                  <th className='text-right'>{ColumnHeaders.COST}</th>
                </tr>
              </thead>
              <tbody>
                {orderedRestockReport.map((result, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        {result.productName}
                        <div className='text-muted'>{result.sku || '-'}</div>
                      </td>
                      <td>{result.supplierName}</td>
                      <td>{result.categoryName}</td>
                      <td>{result.locationName}</td>
                      <td className='text-right'>{result.currentQuantity}</td>
                      <td className='text-right text-muted'>{result.lowStockQuantity}</td>
                      <td className='text-right text-muted'>{result.restockQuantity}</td>
                      <td className='text-right'>
                        <b>{result.restockQuantity - result.currentQuantity}</b>
                      </td>
                      <td className='text-right'>
                        <Currency centValue={result.purchaseTotal} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }
}
